import React from "react";
import { View, StyleSheet } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { ShiftType } from "@/types/db/shifts";
import { WeekBanner } from "@/components/Calendar/WeekBanner";
import { MonthBanner } from "@/components/Calendar/MonthBanner";
import Swimlane from "@/components/Calendar/Swimlane";
import { CardProps } from "@/components/Card";
import { pad } from "@/lib/helpers";
import useDateTime from "@/hooks/useDateTime";
import { useCalendarParams } from "@/lib/calendar";
import { Visible } from "@/components/Table/types";
import { hasStaffingIssue } from "@/services/shiftService";

export type KanbanViewProps = {
  events?: ShiftType[];
  allEvents?: ShiftType[];
  onShiftPress?: (shiftId: string) => void;
  shiftDisplayMode?: "start" | "all";
  visibleFields?: Visible<string>[];
  shiftStats?: {
    pastShifts: number;
    pastIssues: number;
    futureShifts: number;
    futureIssues: number;
  };
};

export function KanbanView({
  events = [],
  allEvents,
  onShiftPress,
  shiftDisplayMode = "start",
  visibleFields,
  shiftStats,
}: KanbanViewProps) {
  const { colors } = useTheme();
  const { isToday, isPast, settings } = useDateTime();
  const { date, daysView } = useCalendarParams();
  const timezone = settings.timezone;

  // Helper to process events for display
  const processEvents = (eventsToProcess: ShiftType[]) =>
    eventsToProcess
      ?.map((event) => {
        const startDate = new Date(event?.start_time ?? "");
        const endDate = new Date(event?.end_time ?? "");

        const startDateParts = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).formatToParts(startDate);

        const endDateParts = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).formatToParts(endDate);

        const timeParts = new Intl.DateTimeFormat("en-US", {
          timeZone: timezone,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).formatToParts(startDate);

        const getPart = (parts: Intl.DateTimeFormatPart[], type: string) =>
          parseInt(parts.find((p) => p.type === type)?.value ?? "0");

        const startYear = getPart(startDateParts, "year");
        const startMonth = getPart(startDateParts, "month");
        const startDay = getPart(startDateParts, "day");
        const startHour = getPart(timeParts, "hour");
        const startMinute = getPart(timeParts, "minute");

        const endYear = getPart(endDateParts, "year");
        const endMonth = getPart(endDateParts, "month");
        const endDay = getPart(endDateParts, "day");

        const startLocal = new Date(startYear, startMonth - 1, startDay);
        const endLocal = new Date(endYear, endMonth - 1, endDay);

        // Check if multi-day shift
        const isMultiDay = startDay !== endDay || startMonth !== endMonth || startYear !== endYear;

        if (shiftDisplayMode === "all" && isMultiDay) {
          // Show on all days between start and end
          const msInDay = 1000 * 60 * 60 * 24;
          const span = Math.floor((endLocal.getTime() - startLocal.getTime()) / msInDay);

          return Array.from({ length: span + 1 }, (_, i) => {
            const nextDay = new Date(startLocal);
            nextDay.setDate(startLocal.getDate() + i);

            return {
              ...event,
              displayYear: nextDay.getFullYear(),
              displayMonth: nextDay.getMonth() + 1,
              displayDay: nextDay.getDate(),
              startHour,
              startMinute,
              isMultiDay,
              dayIndex: i,
            };
          });
        } else {
          // Show only on start day (default behavior)
          return [
            {
              ...event,
              displayYear: startYear,
              displayMonth: startMonth,
              displayDay: startDay,
              startHour,
              startMinute,
              isMultiDay,
              dayIndex: 0,
            },
          ];
        }
      })
      .flat();

  // Process filtered and all events
  const processedEvents = processEvents(events);
  const allProcessedEvents = allEvents ? processEvents(allEvents) : processedEvents;

  // Generate calendar days
  const calendarDays = Array.from({ length: daysView }, (_, i) => {
    const newDate = new Date(date);
    newDate.setDate(date.getDate() + i);
    const parts = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: timezone,
    }).formatToParts(newDate);

    const monthShort = new Intl.DateTimeFormat("en-US", {
      month: "short",
      timeZone: timezone,
    }).format(newDate);

    return {
      weekday: parts.find((p) => p.type === "weekday")?.value ?? "",
      day: parseInt(parts.find((p) => p.type === "day")?.value ?? ""),
      month: parseInt(parts.find((p) => p.type === "month")?.value ?? ""),
      monthShort,
      year: parseInt(parts.find((p) => p.type === "year")?.value ?? ""),
      isWeekend: parts.find((p) => p.type === "weekday")?.value?.[0] === "S",
      date: newDate,
    };
  });

  // Use global shift stats from props (fetched from service)
  const { pastShifts = 0, pastIssues = 0, futureShifts = 0, futureIssues = 0 } = shiftStats || {};

  return (
    <View style={styles.container}>
      <MonthBanner
        pastShifts={pastShifts}
        pastIssues={pastIssues}
        futureShifts={futureShifts}
        futureIssues={futureIssues}
      />
      <WeekBanner />
      <View style={styles.swimlanes}>
        {calendarDays.map((item, index) => {
          const dayEvents =
            processedEvents?.filter(
              (s) => s.displayDay === item.day && s.displayMonth === item.month && s.displayYear === item.year,
            ) || [];

          const totalDayEvents =
            allProcessedEvents?.filter(
              (s) => s.displayDay === item.day && s.displayMonth === item.month && s.displayYear === item.year,
            ) || [];

          const hiddenCount = totalDayEvents.length - dayEvents.length;

          const cards: CardProps[] = dayEvents.map((s) => ({
            shift: s,
            message: `${s.project?.name || "Untitled Project"}`,
            time: `${pad(s.startHour)}:${pad(s.startMinute)}`,
            onPress: () => s.id && onShiftPress?.(s.id),
            positions: s.shift_positions || [],
            visibleFields: visibleFields,
          }));

          const isTodayDate = isToday(item.date);
          const isPastDate = isPast(item.date);

          // Calculate actual issue count from shift data for this day
          const issueCount = dayEvents.filter((s) => hasStaffingIssue(s)).length;

          return (
            <Swimlane
              key={index}
              style={[!item.isWeekend && { backgroundColor: colors.backgroundSecondary }, styles.swimlane]}
              day={item.day}
              weekday={item.weekday}
              month={item.monthShort}
              cards={cards}
              isPast={isPastDate}
              isToday={isTodayDate}
              hiddenCount={hiddenCount}
              issueCount={issueCount}
            />
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 8,
    marginLeft: 56,
  },
  swimlanes: {
    flex: 1,
    flexDirection: "row",
    gap: 8,
  },
  swimlane: {
    flex: 1,
  },
});
